import classnames from 'classnames';
import React, { createContext, memo, useCallback, useContext, useId } from 'react';
import type { TabsContextValue, UnderlinedTabProps, UnderlinedTabsProps } from './types';
import s from './index.module.scss';

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const context = useContext(TabsContext);

  if (!context) {
    throw new Error('UnderlinedTab must be used inside <UnderlinedTabs>');
  }

  return context;
}

function getTabId(baseId: string, value: string | number): string {
  return `${baseId}-tab-${String(value)}`;
}

const UnderlinedTabsRoot = memo(function UnderlinedTabsRoot({
  selected,
  size = 'medium',
  onChange,
  children,
  className,
  'aria-label': ariaLabel,
  ...props
}: UnderlinedTabsProps) {
  const baseId = useId();

  return (
    <TabsContext.Provider value={{ selected, onChange, baseId, size }}>
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={classnames(s.tabs, className)}
        {...props}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
});

const UnderlinedTab = memo(function UnderlinedTab({
  value,
  children,
  className,
  disabled = false,
  onClick,
  ...props
}: UnderlinedTabProps) {
  const { selected, size, onChange, baseId } = useTabsContext();
  const isSelected = selected === value;

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      onChange?.(value);
      onClick?.(event);
    },
    [disabled, onChange, onClick, value],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'Home' && event.key !== 'End') {
        return;
      }

      const list = event.currentTarget.closest('[role="tablist"]');
      if (!list) return;

      const tabs = Array.from(
        list.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])')
      );

      const currentIndex = tabs.indexOf(event.currentTarget);
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;

      if (nextIndex !== currentIndex) {
        const nextTab = tabs[nextIndex];
        if (!nextTab) return;

        event.preventDefault();
        nextTab.focus();

        const nextValue = nextTab.getAttribute('data-value');
        if (nextValue !== null) {
          onChange?.(nextValue);
        }
      }
    },
    [onChange],
  );

  return (
    <button
      id={getTabId(baseId, value)}
      type="button"
      role="tab"
      aria-selected={isSelected}
      tabIndex={isSelected ? 0 : -1}
      disabled={disabled}
      data-value={String(value)}
      data-size={size}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={classnames(
        s.tab,
        size,
        isSelected && s.tab_selected,
        disabled && s.tab_disabled,
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});

type UnderlinedTabsComponent = typeof UnderlinedTabsRoot & {
  Tab: typeof UnderlinedTab;
};

export const UnderlinedTabs = Object.assign(UnderlinedTabsRoot, {
  Tab: UnderlinedTab,
}) as UnderlinedTabsComponent;